// Round 50 — withdrawal and reinstatement: the pure rules both the UI and the
// server read, so the form and the service can never disagree about what a
// valid request is, which reasons exist, or when the cancellation deadline is.
//
// The reasons are tied to the Agreement of Financial Responsibility (Boys:
// legal/approved-2026-10-11/MDBS-financial-responsibility.pdf; Girls:
// shared/templates/girls/financial-responsibility.pdf). Both agreements define
// the same nine "emergency reasons"; the four emergency choices below group
// them, and the two non-emergency choices cover the ordinary cases. Every
// choice also needs a short explanation (ROUND50-PLAN.md §A1).

/** Who asked for the withdrawal or reinstatement. "admin" is for the future Operations Console. */
export type WithdrawalRequester = "parent" | "student" | "admin";

/** The two requesters a family may name on the self-service form. */
export const FAMILY_REQUESTERS = ["parent", "student"] as const;

export type WithdrawalReasonCode =
  | "conflict"
  | "no-longer-attending"
  | "student-illness"
  | "family-illness"
  | "disaster-home"
  | "legal-accident-school"
  | "other";

export interface WithdrawalReason {
  code: WithdrawalReasonCode;
  label: string;
  /** An emergency reason under the Agreement of Financial Responsibility. */
  emergency: boolean;
}

export const WITHDRAWAL_REASONS: readonly WithdrawalReason[] = [
  { code: "conflict", label: "A schedule conflict or another commitment", emergency: false },
  { code: "no-longer-attending", label: "The student has decided not to attend", emergency: false },
  { code: "student-illness", label: "Illness or injury of the student (physician-verified)", emergency: true },
  {
    code: "family-illness",
    label: "Serious illness or injury of an immediate family member (physician-verified)",
    emergency: true,
  },
  {
    code: "disaster-home",
    label: "Disaster, evacuation, government restriction, or damage to or burglary of the home",
    emergency: true,
  },
  {
    code: "legal-accident-school",
    label: "Required court appearance, assault, serious traffic accident, or a school-year extension",
    emergency: true,
  },
  { code: "other", label: "Other", emergency: false },
];

export function withdrawalReason(code: string): WithdrawalReason | undefined {
  return WITHDRAWAL_REASONS.find((r) => r.code === code);
}

export const REASON_TEXT_MIN = 10;
export const REASON_TEXT_MAX = 1000;
export const REQUESTER_NAME_MAX = 120;

/** File types a proof upload may be (checked by content on the server, by name on the client). */
export const PROOF_EXTENSIONS = [".pdf", ".jpg", ".jpeg", ".png"] as const;

/**
 * The sponsor wording the Round 50 acknowledgment and emails use. The portals
 * say "Post or Unit" for both programs everywhere else (ConfirmationScreen,
 * SponsorPanel, Step 1), so this does too.
 */
export const SPONSOR_ENTITY = "Post or Unit";

/** The one-line acknowledgment the family checks (stored verbatim with the request). */
export const WITHDRAWAL_ACK_TEXT =
  `I understand the program does not refund a ${SPONSOR_ENTITY}'s sponsorship. The ${SPONSOR_ENTITY} ` +
  `may apply it to another applicant or keep it as a donation, and anything owed between our family ` +
  `and the ${SPONSOR_ENTITY} is governed by the Agreement of Financial Responsibility.`;

/** Shown above the button: the form itself is the written notice the agreement asks for. */
export const WRITTEN_NOTICE_TEXT =
  "Submitting this form is your written notice of cancellation. The time we receive it is recorded.";

/**
 * The cancellation deadline when CANCELLATION_DEADLINE is unset: 31 days before
 * the session start. Both agreements print "30 days before the start date …, or
 * May 20, 2027" for a June 20 start — and June 20 minus 30 days is May 21. The
 * signed document's printed date is what binds, so the default reproduces it.
 */
export function defaultCancellationDeadline(sessionStartIso: string): string {
  const d = new Date(`${sessionStartIso.slice(0, 10)}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 31);
  return d.toISOString().slice(0, 10);
}

/** Today's date in Maryland ("YYYY-MM-DD", America/New_York). */
export function easternDate(nowMs: number): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(nowMs));
}

/** On or before the date, in Maryland time (the whole day counts). */
export function onOrBeforeEastern(nowMs: number, isoDate: string): boolean {
  return easternDate(nowMs) <= isoDate.slice(0, 10);
}

/** In time under the agreement: received on or before the cancellation deadline (Eastern). */
export function withdrawalInTime(nowMs: number, cancellationDeadline: string): boolean {
  return onOrBeforeEastern(nowMs, cancellationDeadline);
}

/** Withdrawal stays open through the session's last day (owner, 2026-10-21). */
export function withdrawalOpen(nowMs: number, sessionEndDate: string): boolean {
  return onOrBeforeEastern(nowMs, sessionEndDate);
}

/** Statuses a withdrawal can be made from. */
export const WITHDRAWABLE_STATUSES = [
  "InProgress",
  "Incomplete",
  "Submitted",
  "Finalized",
  "AmendmentInProgress",
] as const;

export interface WithdrawalFormInput {
  requestedBy: string;
  requesterName: string;
  reasonCode: string;
  reasonText: string;
  acknowledged: boolean;
}

/**
 * Field-level problems with a withdrawal request, or {} when it is valid.
 * `allowAdmin` is false for the self-service form (a family cannot name "admin").
 */
export function withdrawalFormErrors(
  input: WithdrawalFormInput,
  opts: { allowAdmin?: boolean } = {}
): Partial<Record<keyof WithdrawalFormInput, string>> {
  const errors: Partial<Record<keyof WithdrawalFormInput, string>> = {};
  const roles: string[] = opts.allowAdmin ? [...FAMILY_REQUESTERS, "admin"] : [...FAMILY_REQUESTERS];
  if (!roles.includes(input.requestedBy)) errors.requestedBy = "Choose who is asking.";
  const name = (input.requesterName ?? "").trim();
  if (!name) errors.requesterName = "Enter your name.";
  else if (name.length > REQUESTER_NAME_MAX) errors.requesterName = "That name is too long.";
  if (!withdrawalReason(input.reasonCode)) errors.reasonCode = "Choose a reason.";
  const text = (input.reasonText ?? "").trim();
  if (text.length < REASON_TEXT_MIN) {
    errors.reasonText = `Tell us briefly what happened (at least ${REASON_TEXT_MIN} characters).`;
  } else if (text.length > REASON_TEXT_MAX) {
    errors.reasonText = `Please keep this under ${REASON_TEXT_MAX} characters.`;
  }
  if (!input.acknowledged) errors.acknowledged = "Please check the acknowledgment.";
  return errors;
}

/** Who-asked phrasing for emails and screens, e.g. "Pat Lee, the student's parent or guardian". */
export function requesterPhrase(requestedBy: WithdrawalRequester, name: string): string {
  const who = name.trim();
  if (requestedBy === "parent") return `${who}, the student's parent or guardian`;
  if (requestedBy === "student") return `${who}, the student`;
  return `program staff (${who})`;
}

/** Plain words for a draft status, used by the reinstatement email and screen. */
export function statusInWords(status: string): string {
  switch (status) {
    case "Submitted":
      return "submitted";
    case "Finalized":
      return "finalized";
    case "AmendmentInProgress":
      return "being amended";
    case "Incomplete":
      return "incomplete (not submitted by the deadline)";
    default:
      return "in progress";
  }
}

// ---- the server-owned record (lives on the draft envelope, ServerMeta.withdrawal) ----

/** Who pressed the button: the family through their private link, or staff (future Operations Console). */
export interface WithdrawalActor {
  via: "family-link" | "admin-key";
  /** The name typed on the form, or the staff member's name. */
  name: string;
  /** Staff identifier when via === "admin-key". */
  adminId?: string;
}

/** A stored proof file (the shape of UploadedFileRef, kept local to avoid a type cycle). */
export interface WithdrawalProofRef {
  filename: string;
  contentType: string;
  sizeBytes: number;
  storageKey: string;
  storageItemId?: string;
  webUrl?: string;
  uploadedAt: string;
}

interface WithdrawalEventBase {
  atUtc: string;
  requestedBy: WithdrawalRequester;
  requesterName: string;
  actor: WithdrawalActor;
}

export interface WithdrawnEvent extends WithdrawalEventBase {
  action: "withdrawn";
  reasonCode: WithdrawalReasonCode;
  reasonLabel: string;
  reasonText: string;
  ackText: string;
  cancellationDeadline: string;
  inTime: boolean;
  proof?: WithdrawalProofRef;
  /** The draft status before withdrawal (what reinstatement restores). */
  priorDraftStatus: string;
  /** The tracker Application Status before withdrawal, when it could be read (e.g. an admin-set "Accepted"). */
  priorTrackerStatus: string | null;
}

export interface ReinstatedEvent extends WithdrawalEventBase {
  action: "reinstated";
  restoredDraftStatus: string;
  /** "restored" | "left" (staff changed it while withdrawn) | "queued" (tracker write still retrying). */
  tracker: "restored" | "left" | "queued";
  trackerStatusNow: string | null;
}

export interface ProofAddedEvent extends WithdrawalEventBase {
  action: "proof-added";
  proof: WithdrawalProofRef;
}

export type WithdrawalEvent = WithdrawnEvent | ReinstatedEvent | ProofAddedEvent;

export interface WithdrawalState {
  /** The withdrawal in force, or null when the application is not withdrawn. */
  current: WithdrawnEvent | null;
  /** Append-only: every withdraw, reinstate and proof upload. Nothing is ever removed. */
  history: WithdrawalEvent[];
}

/** What the resume GET hands the screen: no IP, no user agent, no storage paths. */
export interface WithdrawalSummary {
  withdrawnAtUtc: string;
  requestedBy: WithdrawalRequester;
  requesterName: string;
  reasonLabel: string;
  inTime: boolean;
  cancellationDeadline: string;
  proofCount: number;
}

export function withdrawalSummaryOf(state: WithdrawalState | undefined): WithdrawalSummary | null {
  const cur = state?.current;
  if (!cur) return null;
  // By position, not identity: the envelope is JSON, so `current` and its
  // history entry are separate objects once read back.
  const history = state!.history;
  let since = -1;
  for (let i = history.length - 1; i >= 0; i--) {
    if (history[i].action === "withdrawn") {
      since = i;
      break;
    }
  }
  const proofs =
    (cur.proof ? 1 : 0) + history.slice(since + 1).filter((e) => e.action === "proof-added").length;
  return {
    withdrawnAtUtc: cur.atUtc,
    requestedBy: cur.requestedBy,
    requesterName: cur.requesterName,
    reasonLabel: cur.reasonLabel,
    inTime: cur.inTime,
    cancellationDeadline: cur.cancellationDeadline,
    proofCount: proofs,
  };
}
